import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { movies, heroMovies, categories, newEpisodes, siteInfo } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Home() {
  const [currentHero, setCurrentHero] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentHero((prev) => (prev + 1) % heroMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const hotMovies = movies.filter((m) => m.isHot).slice(0, 8);
  const newMovies = movies.filter((m) => m.isNew).slice(0, 8);
  const topRated = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 8);

  const jsonLdWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '7777影院 - 免费电影高清在线观看首页',
    description: siteInfo.description,
    url: 'https://www.7777yingyuan.com/',
    isPartOf: { '@type': 'WebSite', name: '7777影院', url: 'https://www.7777yingyuan.com/' },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLdWebPage)}</script>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          {heroMovies.map((movie: Movie, index: number) => (
            <div
              key={movie.id}
              className={`absolute inset-0 transition-opacity duration-700 ${index === currentHero ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            >
              <img
                src={movie.imageUrl}
                alt={movie.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50" />
            </div>
          ))}

          <div className="absolute inset-0 z-20 flex items-center">
            <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  {heroMovies[currentHero].isNew && (
                    <span className="bg-accent-500 text-white text-xs font-bold px-3 py-1 rounded whitespace-nowrap">最新上映</span>
                  )}
                  {heroMovies[currentHero].isHot && (
                    <span className="bg-primary-500 text-background-950 text-xs font-bold px-3 py-1 rounded whitespace-nowrap">热播推荐</span>
                  )}
                  <span className="text-sm text-foreground-200">{heroMovies[currentHero].year}</span>
                  <span className="text-sm text-foreground-200">{heroMovies[currentHero].duration}</span>
                </div>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3 tracking-wide">
                  {heroMovies[currentHero].title}
                </h1>
                <p className="text-sm md:text-base text-foreground-200 mb-6 line-clamp-2 max-w-xl">
                  {heroMovies[currentHero].description}
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/detail/${heroMovies[currentHero].id}`}
                    className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-950 font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-fill text-lg"></i>
                    <span className="text-sm">立即观看</span>
                  </Link>
                  <Link
                    to={`/detail/${heroMovies[currentHero].id}`}
                    className="flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-medium px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap border border-white/20"
                  >
                    <i className="ri-information-line text-lg"></i>
                    <span className="text-sm">了解详情</span>
                  </Link>
                </div>

                <div className="flex items-center gap-2 mt-8">
                  {heroMovies.map((_: Movie, index: number) => (
                    <button
                      key={index}
                      onClick={() => setCurrentHero(index)}
                      className={`h-1 rounded-full transition-all cursor-pointer ${index === currentHero ? 'w-8 bg-primary-500' : 'w-4 bg-white/40 hover:bg-white/60'}`}
                      aria-label={`切换到第${index + 1}张`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-8">
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.key}
                  to={cat.key === 'all' ? '/category/all' : `/category/${cat.key}`}
                  className="flex items-center gap-2 bg-background-100 hover:bg-primary-500/10 border border-background-200/50 hover:border-primary-500/30 text-foreground-300 hover:text-primary-400 px-4 py-2.5 rounded-full transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  <span className="text-sm font-medium">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Movies */}
        <section className="w-full bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 pb-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary-500 rounded-full" />
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  热播推荐
                </h2>
              </div>
              <Link to="/category/all" className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                <span>查看全部</span>
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* New Movies */}
        <section className="w-full bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-accent-500 rounded-full" />
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  最新上映
                </h2>
              </div>
              <Link to="/category/all" className="flex items-center gap-1 text-sm text-foreground-500 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap">
                <span>查看全部</span>
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Top Rated */}
        <section className="w-full bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-secondary-500 rounded-full" />
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  高分佳作
                </h2>
              </div>
              <Link to="/category/drama" className="flex items-center gap-1 text-sm text-foreground-500 hover:text-secondary-400 transition-colors cursor-pointer whitespace-nowrap">
                <span>查看全部</span>
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {topRated.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* TV Series */}
        <section className="w-full bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary-500 rounded-full" />
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50">
                  热播电视剧
                </h2>
              </div>
              <Link to="/category/tv" className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
                <span>查看全部</span>
                <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {newEpisodes.map((ep) => (
                <Link
                  key={ep.id}
                  to={`/detail/${ep.id}`}
                  className="group block cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={ep.imageUrl}
                      alt={ep.title}
                      title={`${ep.title} - 7777影院高清在线观看`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-primary-500 text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
                      {ep.rating}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-3">
                      <span className="text-xs text-primary-400 font-medium">{ep.episode}</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-100 group-hover:text-primary-400 transition-colors line-clamp-1">
                    {ep.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">{stat.value}</div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="w-full bg-background-100">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-14">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-3">
                为什么选择7777影院
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                我们致力于提供最好的免费在线观影体验，让每个人都能享受高品质影视内容
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {siteInfo.features.map((feature) => (
                <div key={feature.title} className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/50 hover:border-primary-500/20 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-500 mb-4">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-100 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}